// src/views/Users/Customers/Modal/AddProjectModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { modeConstants, recruiterConstants } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { candidateActions } from "../../redux/actions";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import { industryOptions } from "../utility";
import { ToastifyService } from "../../services";
import { getAllCurrencies } from "../utility";
import JoditEditorComponent from "../SummernoteEditor";

const DEFAULT_VALUES = {
  candidateName: "",
  candidateEmail: "",
  contactNumber: "",
  country: "",
  state: "",
  city: "",
  gender: "",
  nationality: "",
  currentDesignation: "",
  industry: "",
  location: "",
  currentCompany: "",
  currentSalary: 0.0,
  expectedSalary: 0.0,
  appliedPosition: "",
  detail: "",
  currency: "",
  photo: null,
  projectIds: [],
};

const genderOptions = ["Male", "Female", "Other"];

export default function AddCandidateModal({
  show,
  onHide,
  mode,
  isProjectDataAvailable = true,
}) {
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;

  const [countryCode, setCountryCode] = useState("pk");
  const [previewUrl, setPreviewUrl] = useState("");

  // NEW: hold the CV file (we send this in final submit)
  const [cvFile, setCvFile] = useState(null);
  const [cvDragOver, setCvDragOver] = useState(false);
  const cvInputRef = useRef(null);

  const dispatch = useDispatch();
  const initialData = useSelector((s) => s.candidateReducer.formData);
  const projectData = useSelector((s) => s.projectReducer.formData);
  const allprojectData = useSelector((s) => s.projectReducer.allData);
  const profileData = useSelector((s) => s.userReducer.profileData);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES, mode: "onChange",          // validate on typing
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  // build avatar preview when photo changes
  const photoFile = watch("photo");
  useEffect(() => {
    if (!photoFile || typeof photoFile === "string") return;
    const file = Array.isArray(photoFile) ? photoFile[0] : photoFile;
    if (!(file instanceof File)) return;

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  // prefill on open
  useEffect(() => {
    if (!show) return;

    if (isAdd) {
      reset(DEFAULT_VALUES);
      setPreviewUrl("");
      setCvFile(null);
      return;
    }

    if (initialData && (isEdit || isView)) {
      reset({
        candidateName: initialData.name ?? "",
        candidateEmail: initialData.email ?? "",
        contactNumber: String(initialData.contactNumber ?? "") ?? "",
        country: initialData.country ?? "",
        state: initialData.state ?? "",
        city: initialData.city ?? "",
        gender: initialData.gender ?? "",
        nationality: initialData.nationality ?? "",
        currentDesignation: initialData.designation ?? "",
        industry: initialData?.candidate?.industry ?? "",
        location: initialData.location ?? "",
        currentCompany: initialData?.candidate?.currentCompany ?? "",
        currentSalary: initialData?.candidate?.currentSalary ?? 0,
        expectedSalary: initialData?.candidate?.expectedSalary ?? 0,
        appliedPosition: initialData?.candidate?.appliedPosition ?? "",
        detail: initialData.details ?? "",
        currency: initialData?.candidate?.currency ?? "",
        photo: null,
      });
      if (initialData?.projectCandidates?.length > 0) {
        const ids = initialData.projectCandidates
          .map((p) => p.projectId)
          .filter((id) => id != null);

        setValue("projectIds", ids, { shouldValidate: false });
      } else {
        // ensure it's always an array
        setValue("projectIds", [], { shouldValidate: false });
      }
      if (initialData?.picUrl) {
        setPreviewUrl(process.env.REACT_APP_STORAGE_URL + initialData?.picUrl);
      } else {
        setPreviewUrl("");
      }

      // We’re not pre-filling a file; keep null until user drops/selects a new CV
      setCvFile(null);
      return;
    }

    reset(DEFAULT_VALUES);
    setPreviewUrl("");
    setCvFile(null);
  }, [show, isAdd, isEdit, isView, initialData, reset]);

  // helpers
  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizes.length - 1
    );
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${sizes[i]}`;
  };

  // select file (click-browse)
  const onCvInputChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      // TODO: replace with your toast system
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setCvFile(f);
  };

  // drag & drop
  const onCvDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCvDragOver(true);
  };
  const onCvDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCvDragOver(false);
  };
  const onCvDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCvDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setCvFile(f);
  };

  const onSubmit = async (values) => {
    let projId = isProjectDataAvailable ? projectData?.id : 0;
    const fd = new FormData();
    // top-level
    fd.append("Name", values.candidateName || "");
    fd.append("UserTypeId", recruiterConstants.CANDIDATE);
    fd.append("Email", values.candidateEmail || "");
    fd.append("ContactNumber", values.contactNumber || "");
    fd.append("Country", values.country || "");
    fd.append("State", values.state || "");
    fd.append("City", values.city || "");
    fd.append("Gender", values.gender || "");
    fd.append("Nationality", values.nationality || "");
    fd.append("Designation", values.currentDesignation || "");
    fd.append("Location", values.location || "");
    fd.append("Details", values.detail || "");

    // nested Candidate.*
    fd.append("Candidate.Industry", values.industry || "");
    fd.append("Candidate.CurrentCompany", values.currentCompany || "");
    fd.append("Candidate.CurrentSalary", values.currentSalary || 0);
    fd.append("Candidate.ExpectedSalary", values.expectedSalary || 0);
    fd.append("Candidate.AppliedPosition", values.appliedPosition || "");
    fd.append("Candidate.Currency", values.currency || "");
    fd.append(
      "Candidate.RecruiterId",
      initialData?.candidate?.recruiterId || profileData.id
    );

    let cv = cvFile;
    if (cv && !(cv instanceof File) && cv?.[0]) cv = cv[0];
    if (cv instanceof File) {
      // Adjust field name if your API expects a different one (e.g., "Cv" or "CvFile")
      fd.append("Candidate.Cv", cv, cv.name);
    } else if (initialData?.candidate?.cvUrl && isEdit) {
      fd.append("Candidate.CVUrl", initialData?.candidate?.cvUrl);
    }

    // photo handling
    let file = values.photo;
    if (file && !(file instanceof File) && file?.[0]) file = file[0];
    if (file instanceof File) {
      fd.append("Pic", file, file.name);
    } else if (initialData?.picUrl && isEdit) {
      fd.append("PicUrl", initialData.picUrl);
    }

    // project ids
    (values.projectIds || []).forEach((id, idx) => {
      fd.append(`ProjectCandidates[${idx}].ProjectId`, String(id));
    });

    // edit identifiers
    if (isEdit && initialData?.id) {
      fd.append("Id", String(initialData.id));
      fd.append("Candidate.Id", String(initialData?.candidate?.id));
    }

    if (isEdit) {
      dispatch(candidateActions.updateCandidate(fd, projId, onHide));
    } else {
      dispatch(candidateActions.createCandidate(fd, projId, onHide));
    }
  };

  const title = isAdd
    ? "Add Candidate"
    : isEdit
      ? "Edit Candidate"
      : "View Candidate";

  const currencies = getAllCurrencies();

  // Map to react-select format
  const options = currencies.map((c) => ({
    value: c.currency,
    label: `${c.currency} - ${c.name} (${c.symbol})`,
  }));

  useEffect(() => {
    if (show && isProjectDataAvailable && projectData?.id) {
      setValue("projectIds", [projectData.id], { shouldValidate: true });
    }
  }, [show, projectData]);

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-3">
              <div className="col-lg-9">
                <div className="row g-3">
                  {/* Row 1 */}
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="candidateName"
                        className="form-control"
                        placeholder="Candidate Name"
                        {...register("candidateName", {
                          required: "The field is required",
                          maxLength: {
                            value: 50,
                            message: "Maximum 50 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="candidateName">Candidate Name</label>
                    </div>
                    {errors.candidateName && (
                      <small className="text-danger">
                        {errors.candidateName.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="email"
                        type="email"
                        className="form-control"
                        placeholder="Candidate Email"
                        {...register("candidateEmail", {
                          required: "The field is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Invalid email",
                          },
                        })}
                      />
                      <label htmlFor="email">Email</label>
                    </div>
                    {errors.candidateEmail && (
                      <small className="text-danger">
                        {errors.candidateEmail.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <Controller
                      name="contactNumber"
                      control={control}
                      rules={{
                        required: "The field is required",
                        validate: (val) =>
                          (val && val.length >= 8) || "Invalid number",
                      }}
                      render={({ field: { value, onChange } }) => (
                        <PhoneInput
                          country={countryCode}
                          value={(value ?? "").toString()}
                          onChange={(val, country) => {
                            onChange(val);
                            if (country?.countryCode)
                              setCountryCode(country.countryCode);
                          }}
                          inputProps={{
                            name: "contactNumber",
                            required: true,
                            autoFocus: false,
                          }}
                          placeholder="Enter phone number"
                          enableSearch
                          disableSearchIcon={false}
                          inputStyle={{ width: "100%" }}
                          buttonStyle={{
                            borderTopLeftRadius: 6,
                            borderBottomLeftRadius: 6,
                          }}
                          containerStyle={{ width: "100%" }}
                          disabled={isView}
                        />
                      )}
                    />
                    {errors.contactNumber && (
                      <small className="text-danger">
                        {errors.contactNumber.message}
                      </small>
                    )}
                  </div>

                  {/* Row 2 */}
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="country"
                        className="form-control"
                        placeholder="Country"
                        {...register("country", {
                          required: "The field is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="country">Country</label>
                    </div>
                    {errors.country && (
                      <small className="text-danger">
                        {errors.country.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="state"
                        className="form-control"
                        placeholder="State"
                        {...register("state", {
                          required: "The field is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="state">State</label>
                    </div>
                    {errors.state && (
                      <small className="text-danger">
                        {errors.state.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="city"
                        className="form-control"
                        placeholder="City"
                        {...register("city", {
                          required: "The field is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="city">City</label>
                    </div>
                    {errors.city && (
                      <small className="text-danger">
                        {errors.city.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="location"
                        className="form-control"
                        placeholder="Location"
                        {...register("location", {
                          required: "The field is required",
                        })}
                      />
                      <label htmlFor="location">Location</label>
                    </div>
                    {errors.location && (
                      <small className="text-danger">
                        {errors.location.message}
                      </small>
                    )}
                  </div>

                  {/* Row 3 */}
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <select
                        id="gender"
                        className="form-control"
                        {...register("gender", {
                          required: "The field is required",
                        })}
                      >
                        <option value="">Gender</option>
                        {genderOptions.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="gender">Gender</label>
                    </div>
                    {errors.gender && (
                      <small className="text-danger">
                        {errors.gender.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="nationality"
                        className="form-control"
                        placeholder="Nationality"
                        {...register("nationality", {
                          required: "The field is required",
                          maxLength: {
                            value: 50,
                            message: "Maximum 50 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="nationality">Nationality</label>
                    </div>
                    {errors.nationality && (
                      <small className="text-danger">
                        {errors.nationality.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="designation"
                        className="form-control"
                        placeholder="Current Designation"
                        {...register("currentDesignation", {
                          maxLength: {
                            value: 50,
                            message: "Maximum 50 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="designation">Designation</label>
                    </div>
                    {errors.currentDesignation && (
                      <small className="text-danger">
                        {errors.currentDesignation.message}
                      </small>
                    )}
                  </div>

                  {/* Row 4 */}
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <select
                        id="industry"
                        className="form-control"
                        {...register("industry", {
                          required: "The field is required",
                        })}
                      >
                        <option value="">Select Industry</option>
                        {industryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="industry">Industry</label>
                    </div>
                    {errors.industry && (
                      <small className="text-danger">
                        {errors.industry.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    {projectData && isProjectDataAvailable ? (
                      <>
                        <div
                          className="form-control form-floating"
                          style={{ height: "45px" }}
                        >
                          <span className="badge bg-primary me-1">
                            {projectData?.positionName}
                          </span>
                        </div>
                        {/* <input
                          type="hidden"
                          value={projectData?.id ?? ""}
                          {...register("projectIds.0")}
                        /> */}
                      </>
                    ) : (
                      <Controller
                        name="projectIds"
                        control={control}
                        rules={{
                          required: "Please select at least one project",
                        }}
                        render={({ field }) => {
                          // field.value is the single source of truth (array of ids)
                          const selectedValues = Array.isArray(field.value)
                            ? field.value
                            : [];

                          return (
                            <Select
                              {...field}
                              isMulti
                              classNamePrefix="react-select"
                              options={allprojectData.map((r) => ({
                                value: r.id,
                                label: r.positionName ?? r.positionName,
                              }))}
                              // map form values (ids) -> {value, label} for react-select
                              value={allprojectData
                                .filter((r) => selectedValues.includes(r.id))
                                .map((r) => ({
                                  value: r.id,
                                  label: r.positionName,
                                }))}
                              onChange={(selectedOptions) =>
                                field.onChange(
                                  selectedOptions
                                    ? selectedOptions.map((o) => o.value)
                                    : []
                                )
                              }
                              placeholder="Select Projects..."
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  borderColor: "#ced4da",
                                  boxShadow: "none",
                                  "&:hover": { borderColor: "#86b7fe" },
                                }),
                              }}
                            />
                          );
                        }}
                      />
                    )}
                    {errors.projectIds && (
                      <small className="text-danger">
                        {errors.projectIds.message}
                      </small>
                    )}
                  </div>

                  {/* Row 5 */}
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="currentCompany"
                        className="form-control"
                        placeholder="Current Company"
                        {...register("currentCompany", {
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="currentCompany">Current Company</label>
                    </div>
                    {/* {errors.currentCompany && (
                      <small className="text-danger">
                        {errors.currentCompany.message}
                      </small>
                    )} */}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="currentSalary"
                        className="form-control"
                        placeholder="Current Salary"
                        inputMode="numeric"
                        {...register("currentSalary", {
                          pattern: {
                            value: /^[0-9]*\.?[0-9]+$/,
                            message: "Only numbers or decimals are allowed",
                          },
                        })}
                      />
                      <label htmlFor="currentSalary">Current Salary</label>
                    </div>
                    {errors.currentSalary && (
                      <small className="text-danger">
                        {errors.currentSalary.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="expectedSalary"
                        className="form-control"
                        placeholder="Expected Salary"
                        inputMode="numeric"
                        {...register("expectedSalary", {
                          pattern: {
                            value: /^[0-9]*\.?[0-9]+$/,
                            message: "Only numbers or decimals are allowed",
                          },
                        })}
                      />
                      <label htmlFor="expectedSalary">Expected Salary</label>
                    </div>
                    {errors.expectedSalary && (
                      <small className="text-danger">
                        {errors.expectedSalary.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div className="form-floating">
                          <Select
                            {...field}
                            options={options}
                            isSearchable
                            placeholder="Currency"
                            classNamePrefix="react-select"
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            } // <-- send only code
                            value={
                              options.find(
                                (opt) => opt.value === field.value
                              ) || null
                            }
                          />
                        </div>
                      )}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="position"
                        className="form-control"
                        placeholder="Applied Position"
                        {...register("appliedPosition", {
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed",
                          },
                        })}
                      />
                      <label htmlFor="position">Applied Position</label>
                    </div>
                    {/* {errors.appliedPosition && (
                      <small className="text-danger">
                        {errors.appliedPosition.message}
                      </small>
                    )} */}
                  </div>

                  {/* Row 7: Detail */}

                  <div className="col-12">
                    <div className="form-floating">
                      {/* <textarea
                        id="detail"
                        className="form-control"
                        rows={6}
                        placeholder="Detail"
                        {...register("detail")}
                      /> */}
                      <JoditEditorComponent
                        defaultValue={watch("detail")}
                        onChange={(content) =>
                          setValue("detail", content)
                        }
                        height={250}
                      />
                      {/* <label htmlFor="detail">Details</label> */}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT: avatar + CV dropzone */}
              <div className="col-lg-3 d-flex flex-column align-items-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                  style={{
                    width: 110,
                    height: 110,
                    background: "#eef5ff",
                    overflow: "hidden",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <i
                      className="bi bi-person-fill"
                      style={{ fontSize: 56, color: "#2e6bff" }}
                    />
                  )}
                </div>

                <div className="w-100">
                  <label className="form-label">Upload Pic</label>
                  <input
                    type="file"
                    className="form-control mt-2"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setValue("photo", file, { shouldValidate: false });
                    }}
                  />
                </div>

                {/* CV Upload (Drag & Drop, sends whole file on submit) */}
                <div className="w-100 mt-5">
                  <label className="form-label d-block mb-2">Upload CV</label>

                  {/* Hidden input for click-to-browse */}
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="d-none"
                    disabled={isView}
                    onChange={onCvInputChange}
                  />

                  {/* Dropzone */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => !isView && cvInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && !isView) {
                        cvInputRef.current?.click();
                      }
                    }}
                    onDragOver={onCvDragOver}
                    onDragLeave={onCvDragLeave}
                    onDrop={onCvDrop}
                    className="p-3 text-center"
                    style={{
                      border: "2px dashed",
                      borderColor: cvDragOver ? "#2e6bff" : "#ced4da",
                      borderRadius: 10,
                      background: cvDragOver
                        ? "rgba(46,107,255,0.06)"
                        : "#f8f9fb",
                      transition: "all .15s ease",
                      cursor: isView ? "not-allowed" : "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <i
                        className={`bi ${cvFile
                          ? "bi-file-earmark-check"
                          : "bi-file-earmark-arrow-up"
                          } mb-2`}
                        style={{
                          fontSize: 28,
                          color: cvDragOver ? "#2e6bff" : "#6c757d",
                        }}
                      />
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        {cvFile ? "File selected" : "Drag & drop CV here"}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        or{" "}
                        <span className="text-primary text-decoration-underline">
                          browse
                        </span>{" "}
                        to choose a file
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        Accepted: PDF, DOC, DOCX • Max ~2MB
                      </div>
                      {cvFile && (
                        <div className="mt-2 small">
                          {cvFile.name} • {formatBytes(cvFile.size)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status row */}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                      {cvFile ? "CV ready to upload" : "No CV selected"}
                    </small>

                    {cvFile && !isView && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCvFile(null);
                          if (cvInputRef.current) cvInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                {/* END CV Upload */}
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 footer-modal">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="btn-footer"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create Candidate"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
